/**
 * WebSocket 消息处理 Hook
 * 与旧版 ai-chat.js 对齐：status → liveStatus、thought_chunk 节流到 rAF、工具链清空思考、打字由 AgentLivePanel 承担
 */

import { useEffect, useCallback, useRef } from 'react';
import { useChatStore } from '@/stores';
import type { WSEventData } from '@/types';
import { formatAgentToolLine } from '@/utils/toolDisplayLabels';

interface UseMessagesOptions {
  serverId: number;
  wsClient: {
    on: (event: string, callback: (data: unknown) => void) => void;
    off: (event: string, callback?: (data: unknown) => void) => void;
    socket?: {
      emit: (event: string, data?: unknown) => void;
    };
  } | null;
}

export function useMessages({ serverId, wsClient }: UseMessagesOptions) {
  const sessionId = useChatStore((s) => s.sessionId);

  const thoughtRafRef = useRef<number | null>(null);
  const pendingThoughtRef = useRef<string | null>(null);
  const answerRafRef = useRef<number | null>(null);
  const pendingAnswerRef = useRef<string | null>(null);
  const outputRafRef = useRef<number | null>(null);
  const pendingOutputRef = useRef<string | null>(null);
  /** 回合结束与自动续跑/压缩之间的短暂空窗，避免按钮过早回到「发送」态 */
  const processingIdleTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!wsClient) return;

    const hasRunningBackgroundTasks = () =>
      useChatStore.getState().backgroundTasks.some((t) => t.status === 'running');

    const hasPendingAutoResume = () => {
      const op = useChatStore.getState().currentOperation;
      return !!(op?.pendingResume || op?.phase === 'paused_step_budget' || op?.phase === 'verifying' || op?.phase === 'fixing');
    };

    const clearProcessingIdleTimer = () => {
      if (processingIdleTimerRef.current != null) {
        window.clearTimeout(processingIdleTimerRef.current);
        processingIdleTimerRef.current = null;
      }
    };

    /** 有实时输出/思考/压缩时，强制保持「停止」按钮 */
    const markAgentBusy = () => {
      clearProcessingIdleTimer();
      useChatStore.getState().setIsProcessing(true);
    };

    /**
     * 回合看似结束时不要立刻 idle：自动续跑、上下文压缩、后台任务都可能马上继续。
     * 若短时间内又有活动事件，会取消本次 idle。
     */
    const markAgentMaybeIdle = () => {
      if (hasRunningBackgroundTasks() || hasPendingAutoResume()) {
        markAgentBusy();
        return;
      }
      clearProcessingIdleTimer();
      processingIdleTimerRef.current = window.setTimeout(() => {
        processingIdleTimerRef.current = null;
        if (hasRunningBackgroundTasks() || hasPendingAutoResume()) {
          useChatStore.getState().setIsProcessing(true);
          return;
        }
        useChatStore.getState().setIsProcessing(false);
      }, 2000);
    };

    // 注册 abort 回调到 store，供 AgentLivePanel 停止按钮使用
    useChatStore.getState().setAbortChatFn(() => {
      if (!wsClient?.socket) return;
      const st = useChatStore.getState();
      clearProcessingIdleTimer();
      for (const task of st.backgroundTasks) {
        if (task.status === 'running') {
          wsClient.socket.emit('cancel_task', { task_id: task.taskId });
        }
      }
      wsClient.socket.emit('abort_chat');
      st.setIsProcessing(false);
      st.clearAgentLiveUi();
    });

    const flushThought = () => {
      thoughtRafRef.current = null;
      const t = pendingThoughtRef.current;
      if (t != null) {
        useChatStore.getState().setCurrentThinking(t);
      }
    };

    const scheduleThought = (thought: string) => {
      pendingThoughtRef.current = thought;
      if (thoughtRafRef.current == null) {
        thoughtRafRef.current = requestAnimationFrame(flushThought);
      }
    };

    const flushAnswer = () => {
      answerRafRef.current = null;
      const t = pendingAnswerRef.current;
      if (t != null) {
        useChatStore.getState().setStreamingAnswer(t);
      }
    };

    const scheduleAnswer = (text: string) => {
      pendingAnswerRef.current = text;
      if (answerRafRef.current == null) {
        answerRafRef.current = requestAnimationFrame(flushAnswer);
      }
    };

    const flushOutput = () => {
      outputRafRef.current = null;
      const t = pendingOutputRef.current;
      if (t != null) {
        useChatStore.getState().setLiveCommandOutput(t);
      }
    };

    const scheduleOutput = (chunk: string) => {
      const st = useChatStore.getState();
      pendingOutputRef.current = (st.liveCommandOutput || '') + chunk;
      if (outputRafRef.current == null) {
        outputRafRef.current = requestAnimationFrame(flushOutput);
      }
    };

    const handleStatus = (data: unknown) => {
      const event = data as WSEventData['status'] & { status?: string };
      if (event?.message) {
        useChatStore.getState().setLiveStatus(event.message);
      }
      if (event?.status === 'processing') {
        markAgentBusy();
      }
    };

    const handleThoughtChunk = (data: unknown) => {
      const event = data as WSEventData['thought_chunk'];
      if (event?.thought) {
        markAgentBusy();
        scheduleThought(event.thought);
      }
    };

    const handleToolCall = (data: unknown) => {
      const event = data as WSEventData['tool_call'];
      // 不再清空思考，保留 AI 的推理过程让用户看到"为什么要执行这个命令"
      if (event?.tool === 'check_task_status') return;
      // web_search：与 tool_result 合并为一条助手气泡，避免「工具行 + 结果行」像重复两次
      if (event?.tool === 'web_search') return;
      // execute_command / execute_background_command：command_exec 已显示具体命令，无需再显示工具行
      if (event?.tool === 'execute_command') return;
      if (event?.tool === 'execute_background_command') return;
      if (event?.tool === 'inspect_database_credentials') return;
      if (event?.tool) {
        useChatStore.getState().addMessage({
          role: 'system',
          content: formatAgentToolLine(event.tool),
        });
      }
    };

    const handleCommandExec = (data: unknown) => {
      const event = data as WSEventData['command_exec'] & { background?: boolean };
      if (!event?.command) return;
      markAgentBusy();
      const st = useChatStore.getState();
      const level = event.securityLevel || 'SAFE';
      const levelIcon =
        level === 'HIGH' ? '!' : level === 'MEDIUM' ? '⚠' : '✓';
      // 后台命令：显示"后台执行"标记，不设置 currentCommand（输出走 task_progress 而非 command_output）
      if (event.background) {
        st.addMessage({
          role: 'system',
          content: `${levelIcon} [后台] ${event.command.substring(0, 120)}`,
        });
        return;
      }
      // Cursor 风格：命令行只显示在实时面板标题，不再单独添加 system 消息
      // 命令完成后会打包成 commandBlock 消息存入列表
      st.setCurrentCommand(event.command);
      st.setCommandOutputDone(false);
    };

    const handleCommandOutput = (data: unknown) => {
      const event = data as { chunk: string; type: 'stdout' | 'stderr'; sessionId?: string };
      if (event?.chunk) {
        markAgentBusy();
        scheduleOutput(event.chunk);
      }
    };

    const handleToolResult = (data: unknown) => {
      const event = data as WSEventData['tool_result'];
      const st = useChatStore.getState();
      // execute_command 完成时：Cursor 风格 — 打包成可折叠的命令块存入消息列表，清空实时面板
      if (event?.tool === 'execute_command') {
        const result = event.result as {
          success: boolean;
          exitCode?: number;
          error?: string;
          output?: string;
          command?: string;
          taskId?: string;
          status?: string;
          message?: string;
          output_tail?: string;
          autoBackground?: boolean;
        };
        if (result?.autoBackground && result.taskId) {
          const lines = [
            '检测到这是长时间命令，已自动转为后台任务。',
            `任务 ID: ${result.taskId}`,
          ];
          if (result.message) lines.push(result.message);
          if (result.output_tail && String(result.output_tail).trim()) {
            lines.push('--- 首批输出 ---');
            lines.push(String(result.output_tail).substring(0, 2000));
          }
          st.addMessage({
            role: 'assistant',
            content: lines.join('\n'),
            assistantKind: 'tool_output',
          });
          st.setCommandOutputDone(false);
          st.setCurrentCommand(null);
          st.setLiveCommandOutput(null);
          return;
        }
        // 获取当前实时面板累积的输出（含 ANSI 颜色码）
        const currentOutput = st.liveCommandOutput || '';
        const command =
          st.currentCommand ||
          result?.command ||
          ((event as unknown as { input?: { command?: string } }).input?.command) ||
          '';
        if (!command.trim() && !currentOutput && (result?.output || result?.error)) {
          st.addMessage({
            role: 'assistant',
            content: result.success
              ? (result.output || '(无输出)')
              : `✗ ${result.error || result.output || '命令执行失败'}`,
            assistantKind: 'tool_output',
          });
          st.setCommandOutputDone(false);
          st.setCurrentCommand(null);
          st.setLiveCommandOutput(null);
          return;
        }

        // 打包成 commandBlock 消息（可折叠，默认收起）
        st.addMessage({
          role: 'assistant',
          content: '',
          commandBlock: {
            command,
            output: currentOutput || result?.output || '',
            exitCode: result?.exitCode ?? null,
            success: result?.success ?? false,
            error: result?.error,
          },
        });

        // 清空实时面板（输出已转移到消息列表）
        st.setCommandOutputDone(false);
        st.setCurrentCommand(null);
        st.setLiveCommandOutput(null);
        return;
      }

      // execute_background_command 启动结果：显示任务已启动信息
      if (event?.tool === 'execute_background_command') {
        const result = event.result as {
          success: boolean;
          taskId?: string;
          status?: string;
          message?: string;
          error?: string;
          output_tail?: string;
        };
        if (!result) {
          st.addMessage({ role: 'assistant', content: '(无结果)', assistantKind: 'tool_output' });
          return;
        }
        if (!result.success) {
          st.addMessage({
            role: 'system',
            content: `✗ 后台任务启动失败：${(result.error || '').substring(0, 100)}`,
          });
          return;
        }
        const lines: string[] = [];
        if (result.message) lines.push(result.message);
        if (result.taskId) lines.push(`任务 ID: ${result.taskId}`);
        if (result.output_tail && String(result.output_tail).trim()) {
          lines.push('--- 首批输出 ---');
          lines.push(String(result.output_tail).substring(0, 2000));
        }
        st.addMessage({
          role: 'assistant',
          content: lines.join('\n'),
          assistantKind: 'tool_output',
        });
        return;
      }

      if (!event?.result) {
        st.addMessage({
          role: 'assistant',
          content: '(无结果)',
          assistantKind: 'tool_output',
        });
        return;
      }

      const result = event.result as {
        success: boolean;
        status?: string;
        taskId?: string;
        duration?: number;
        output?: string;
        output_tail?: string;
        data?: unknown;
        message?: string;
        error?: string;
        pollCount?: number;
        exitCode?: number;
      };

      // 后台轮询结果必须在聊天里可见（含 running），否则用户以为「查了没反应」
      if (event.tool === 'check_task_status') {
        const header = formatAgentToolLine('check_task_status');
        let body: string;
        if (!result.success) {
          body = `✗ ${result.error || '失败'}`;
        } else {
          const lines: string[] = [];
          if (result.taskId) lines.push(`任务 ID: ${result.taskId}`);
          if (result.status) lines.push(`状态: ${result.status}`);
          if (result.pollCount != null) lines.push(`轮询: 第 ${result.pollCount} 次`);
          if (result.message) lines.push(String(result.message));
          if (typeof result.duration === 'number' && result.duration >= 0) {
            lines.push(`已运行约 ${Math.round(result.duration / 1000)} 秒`);
          }
          const tail =
            (result.output_tail && String(result.output_tail).trim()) ||
            (result.output && String(result.output).trim()) ||
            '';
          if (tail) {
            lines.push('--- 近期输出 ---');
            lines.push(tail);
          }
          if (result.status && result.status !== 'running' && result.exitCode != null) {
            lines.push(`退出码: ${result.exitCode}`);
          }
          body = lines.length ? lines.join('\n') : '(无输出)';
        }
        let text = `${header}\n\n${body}`;
        if (text.length > 5000) {
          text = `${text.slice(0, 5000)}\n... (已截断)`;
        }
        st.addMessage({
          role: 'assistant',
          content: text,
          assistantKind: 'tool_output',
        });
        return;
      }

      const wsResult = result as typeof result & {
        summary?: string;
        results?: unknown;
        query?: string;
      };

      let text: string;
      if (event.tool === 'web_search' && result.success) {
        const chunks: string[] = [];
        if (wsResult.message) chunks.push(String(wsResult.message));
        if (wsResult.summary && String(wsResult.summary).trim()) {
          chunks.push(String(wsResult.summary));
        } else if (wsResult.results != null) {
          chunks.push(
            typeof wsResult.results === 'string'
              ? wsResult.results
              : JSON.stringify(wsResult.results, null, 2)
          );
        }
        text = chunks.join('\n\n').trim() || '(无输出)';
      } else {
        const output = result.success
          ? result.output ||
            (result.data != null ? JSON.stringify(result.data, null, 2) : '') ||
            result.message ||
            '(无输出)'
          : `✗ ${result.error || '失败'}`;
        text = typeof output === 'string' ? output : JSON.stringify(output, null, 2);
      }

      if (text.length > 5000) {
        text = `${text.slice(0, 5000)}\n... (已截断)`;
      }
      if (event.tool === 'web_search') {
        const header = formatAgentToolLine('web_search');
        text = text.trim() ? `${header}\n\n${text}` : header;
      }
      st.addMessage({
        role: 'assistant',
        content: text,
        assistantKind: 'tool_output',
      });
    };

    const handleFinalAnswer = (data: unknown) => {
      const event = data as WSEventData['final_answer'];
      const st = useChatStore.getState();
      if (thoughtRafRef.current != null) {
        cancelAnimationFrame(thoughtRafRef.current);
        thoughtRafRef.current = null;
      }
      pendingThoughtRef.current = null;
      // 清除流式答案残留（若同时存在流式和降级路径）
      st.setStreamingAnswer(null);
      if (event?.answer) {
        st.addMessage({
          role: 'assistant',
          content: event.answer,
          assistantKind: 'final_answer',
        });
      }
      markAgentMaybeIdle();
      st.clearAgentLiveUi();
      st.setPendingMessage(null);
    };

    const handleFinalAnswerChunk = (data: unknown) => {
      const event = data as WSEventData['final_answer_chunk'];
      if (!event) return;
      markAgentBusy();
      // 优先使用 full 字段（完整累积文本），否则追加 delta
      if (event.full != null) {
        scheduleAnswer(event.full);
      } else if (event.delta) {
        const st = useChatStore.getState();
        scheduleAnswer((st.streamingAnswer || '') + event.delta);
      }
    };

    const handleFinalAnswerDone = (data: unknown) => {
      const event = data as WSEventData['final_answer_done'];
      const st = useChatStore.getState();
      if (thoughtRafRef.current != null) {
        cancelAnimationFrame(thoughtRafRef.current);
        thoughtRafRef.current = null;
      }
      pendingThoughtRef.current = null;
      if (answerRafRef.current != null) {
        cancelAnimationFrame(answerRafRef.current);
        answerRafRef.current = null;
      }
      pendingAnswerRef.current = null;
      if (outputRafRef.current != null) {
        cancelAnimationFrame(outputRafRef.current);
        outputRafRef.current = null;
      }
      pendingOutputRef.current = null;
      // 转为正式消息并清空流式状态
      st.finalizeAnswer(event?.answer);
      markAgentMaybeIdle();
      st.clearAgentLiveUi();
      st.setPendingMessage(null);
    };

    const handleConfirmationRequired = (data: unknown) => {
      const event = data as WSEventData['confirmation_required'];
      const st = useChatStore.getState();
      st.setCurrentThinking(null);
      pendingThoughtRef.current = null;
      if (event) {
        st.showConfirmation(event);
      }
    };

    const handleConfirmationProcessed = (data: unknown) => {
      const event = data as WSEventData['confirmation_processed'];
      const st = useChatStore.getState();
      st.hideConfirmation();
      if (event?.approved) {
        st.addMessage({ role: 'system', content: '✓ 用户已确认，继续执行...' });
      } else {
        st.addMessage({ role: 'system', content: '✗ 用户已拒绝执行' });
      }
    };

    const handleCommandNeedsConfirmation = (data: unknown) => {
      const event = data as WSEventData['command_needs_confirmation'];
      if (!event) return;
      const levelIcon =
        event.level === 'HIGH' ? '⚠️' : event.level === 'MEDIUM' ? '⚡' : 'ℹ️';
      const cmd = (event.command || '').substring(0, 50);
      useChatStore.getState().addMessage({
        role: 'system',
        content: `${levelIcon} 等待确认: ${cmd}`,
      });
    };

    const handleInteractivePrompt = (data: unknown) => {
      const event = data as WSEventData['interactive_prompt'];
      const st = useChatStore.getState();
      st.setCurrentThinking(null);
      pendingThoughtRef.current = null;
      if (event) {
        st.showInteractivePrompt(event);
      }
    };

    const handleTaskNeedsUserInput = (data: unknown) => {
      const event = data as WSEventData['task_needs_user_input'];
      if (!event?.taskId) return;
      const st = useChatStore.getState();
      st.setCurrentThinking(null);
      pendingThoughtRef.current = null;
      st.showInteractivePrompt({
        sessionId: `bg_task_${event.taskId}`,
        prompt: event.matchedLine || '后台任务正在等待输入',
        context: event.outputTail,
        backgroundTaskId: event.taskId,
      });
    };

    const handleChatAborted = (data: unknown) => {
      const event = data as WSEventData['chat_aborted'];
      const st = useChatStore.getState();
      clearProcessingIdleTimer();
      if (thoughtRafRef.current != null) {
        cancelAnimationFrame(thoughtRafRef.current);
        thoughtRafRef.current = null;
      }
      pendingThoughtRef.current = null;
      st.addMessage({
        role: 'system',
        content: `✗ ${event?.message || '对话已中止'}`,
      });
      st.setIsProcessing(false);
      st.clearAgentLiveUi();
    };

    // 修复2：Agent 因明确原因停止时，显示退出原因（非静默退出）
    const handleAgentStopped = (data: unknown) => {
      const event = data as WSEventData['agent_stopped'];
      if (!event) return;
      const st = useChatStore.getState();
      let icon = '⏸';
      let hint = '';
      if (event.reason === 'max_iterations') {
        icon = '⏸';
        hint = '发送"继续"可继续执行。';
        // 步数上限后通常会自动续跑，保持处理中避免按钮闪回「发送」
        markAgentBusy();
      } else if (event.reason === 'stream_error' || event.reason === 'error') {
        icon = '⚠️';
        hint = '请检查错误信息后重试。';
      } else if (event.reason === 'disconnected') {
        icon = '🔌';
        hint = '请检查网络连接后重试。';
      }
      st.addMessage({
        role: 'system',
        content: `${icon} ${event.message || 'Agent 已停止执行'} ${hint}`,
      });
      // 不清除 isProcessing — agent_stopped 之后通常还有 final_answer_done / auto_resume
    };

    const handleServerError = (data: unknown) => {
      const event = data as WSEventData['server_error'] & { code?: string };
      const st = useChatStore.getState();
      const msg = event?.message || '';
      // 用户主动停止时后端可能仍抛出 abort 相关错误，不应作为失败提示
      if (
        event?.code === 'AGENT_ERROR' &&
        (/aborted|abort|用户中止|已中止/i.test(msg))
      ) {
        markAgentMaybeIdle();
        return;
      }
      if (thoughtRafRef.current != null) {
        cancelAnimationFrame(thoughtRafRef.current);
        thoughtRafRef.current = null;
      }
      pendingThoughtRef.current = null;
      st.addMessage({
        role: 'system',
        content: `✗ ${event?.message || '请求失败'}`,
      });
      markAgentMaybeIdle();
      st.clearAgentLiveUi();
    };

    // === 后台任务事件 ===
    const handleTaskStarted = (data: unknown) => {
      const event = data as { taskId?: string; command?: string; name?: string };
      if (!event?.taskId) return;
      const st = useChatStore.getState();
      st.upsertBackgroundTask({
        taskId: event.taskId,
        command: event.command,
        name: event.name,
        status: 'running',
        startTime: Date.now(),
      });
      markAgentBusy();
    };

    const handleTaskPolling = (data: unknown) => {
      const event = data as { taskId?: string; pollCount?: number; duration?: number };
      if (!event?.taskId) return;
      useChatStore.getState().updateBackgroundTask(event.taskId, {
        pollCount: event.pollCount,
        duration: event.duration,
      });
    };

    const handleTaskCompleted = (data: unknown) => {
      const event = data as { taskId?: string; duration?: number; result?: { output?: string; failureSummary?: string } };
      if (!event?.taskId) return;
      useChatStore.getState().updateBackgroundTask(event.taskId, {
        status: 'completed',
        duration: event.duration,
        outputTail: event.result?.output ? String(event.result.output).slice(-5000) : undefined,
      });
      markAgentMaybeIdle();
      // 延迟移除已完成任务（让用户看到完成状态几秒）
      setTimeout(() => {
        useChatStore.getState().removeBackgroundTask(event.taskId!);
      }, 5000);
    };

    const handleTaskFailed = (data: unknown) => {
      const event = data as { taskId?: string; error?: string; output?: string };
      if (!event?.taskId) return;
      const existing = useChatStore.getState().backgroundTasks.find((t) => t.taskId === event.taskId);
      const outputTail = [
        existing?.outputTail || '',
        event.output ? String(event.output).slice(-3000) : '',
        event.error ? `\n[后台任务失败] ${event.error}` : '',
      ].filter(Boolean).join('\n').slice(-5000);
      useChatStore.getState().updateBackgroundTask(event.taskId, {
        status: 'failed',
        outputTail,
      });
      markAgentMaybeIdle();
      setTimeout(() => {
        useChatStore.getState().removeBackgroundTask(event.taskId!);
      }, 5000);
    };

    const handleTaskCancelled = (data: unknown) => {
      const event = data as { taskId?: string };
      if (!event?.taskId) return;
      useChatStore.getState().updateBackgroundTask(event.taskId, {
        status: 'cancelled',
      });
      markAgentMaybeIdle();
      setTimeout(() => {
        useChatStore.getState().removeBackgroundTask(event.taskId!);
      }, 5000);
    };

    // D2: 后台任务实时输出（task_progress 事件 — 之前完全未监听）
    // E3: 实时事件和重放事件均带 seq，传给 store 做去重 + 推进 lastSeq
    const handleTaskProgress = (data: unknown) => {
      const event = data as { taskId?: string; data?: string; type?: string; seq?: number };
      if (!event?.taskId || !event?.data) return;
      markAgentBusy();
      useChatStore.getState().appendBackgroundTaskOutput(event.taskId, event.data, event.type, event.seq);
    };

    // E3: 重连后对账后台任务状态
    const handleRunningTasks = (data: unknown) => {
      const event = data as { tasks?: Array<{ taskId: string; command?: string; name?: string; status?: string; startTime?: number; duration?: number }> };
      if (!event?.tasks) return;
      const st = useChatStore.getState();
      if (event.tasks.some((t) => t.status === 'running' || !t.status)) {
        markAgentBusy();
      }
      const backendTaskIds = new Set(event.tasks.map((t) => t.taskId));
      // 补充后端有但前端没有的任务
      for (const t of event.tasks) {
        const existing = st.backgroundTasks.find((bt) => bt.taskId === t.taskId);
        if (!existing) {
          st.upsertBackgroundTask({
            taskId: t.taskId,
            command: t.command,
            name: t.name,
            status: (t.status as 'running' | 'cancelled') || 'running',
            startTime: t.startTime,
            duration: t.duration,
          });
        }
      }
      // 对账：本地仍显示 running、但后端运行列表已不存在的任务，说明完成/失败事件可能断线时漏掉了。
      // 先移出“运行中”视图，避免页面长期显示僵尸后台任务。
      for (const local of st.backgroundTasks) {
        if (local.status !== 'running' || backendTaskIds.has(local.taskId)) continue;
        st.updateBackgroundTask(local.taskId, {
          status: 'failed',
          outputTail: [
            local.outputTail || '',
            '\n[状态对账] 后端运行列表中已不存在该任务，可能已结束或服务重启导致状态丢失。',
          ].join('').slice(-5000),
        });
        setTimeout(() => {
          useChatStore.getState().removeBackgroundTask(local.taskId);
        }, 5000);
      }
      // E2: 对每个 running 任务请求事件重放
      for (const t of event.tasks) {
        const existing = st.backgroundTasks.find((bt) => bt.taskId === t.taskId);
        const fromSeq = existing?.lastSeq || 0;
        wsClient?.socket?.emit('replay_events', { taskId: t.taskId, fromSeq });
      }
    };

    const handleRunningOperations = (data: unknown) => {
      const event = data as WSEventData['running_operations'];
      const first = event?.operations?.[0] || null;
      useChatStore.getState().setCurrentOperation(first);
    };

    const handleOperationUpdate = (data: unknown) => {
      const event = data as WSEventData['operation_update'];
      if (!event?.operationId) return;
      useChatStore.getState().setCurrentOperation(event);
    };

    const handleOperationAutoResume = (data: unknown) => {
      const event = data as WSEventData['operation_auto_resume_prompt'];
      if (!event?.operationId || !event.prompt || !wsClient?.socket) return;
      const st = useChatStore.getState();
      st.addMessage({
        role: 'system',
        content: `后台任务已${event.phase === 'verifying' ? '完成' : '失败'}，正在自动恢复 AI 继续处理...`,
      });
      markAgentBusy();
      st.clearAgentLiveUi();
      wsClient.socket.emit('chat', {
        server_id: event.server_id || serverId,
        message: event.prompt,
        session_id: st.sessionId,
        mode: st.chatMode,
        operation_id: event.operationId,
        auto_resume: true,
      });
    };

    const handleContextUsage = (data: unknown) => {
      const event = data as { usedBytes?: number };
      if (typeof event?.usedBytes !== 'number' || !Number.isFinite(event.usedBytes)) return;
      useChatStore.getState().setServerContextBytes(Math.max(0, event.usedBytes));
    };

    const handleContextCompressed = (data: unknown) => {
      const event = data as { removedCount?: number; summaryLength?: number; remainingMessages?: number };
      markAgentBusy();
      useChatStore.getState().addMessage({
        role: 'system',
        content:
          `已自动压缩旧上下文，移除 ${event?.removedCount || 0} 条旧记录，` +
          `保留 ${event?.remainingMessages || 0} 条最近记录和摘要。`,
      });
    };

    const handleAgentResumed = (data: unknown) => {
      const event = data as { isRunning?: boolean };
      if (!event?.isRunning) return;
      const st = useChatStore.getState();
      markAgentBusy();
      if (!st.liveStatus) {
        st.setLiveStatus('已重新连接到正在运行的 AI 任务...');
      }
    };

    wsClient.on('status', handleStatus);
    wsClient.on('thought_chunk', handleThoughtChunk);
    wsClient.on('tool_call', handleToolCall);
    wsClient.on('command_exec', handleCommandExec);
    wsClient.on('command_output', handleCommandOutput);
    wsClient.on('tool_result', handleToolResult);
    wsClient.on('final_answer', handleFinalAnswer);
    wsClient.on('final_answer_chunk', handleFinalAnswerChunk);
    wsClient.on('final_answer_done', handleFinalAnswerDone);
    wsClient.on('confirmation_required', handleConfirmationRequired);
    wsClient.on('confirmation_processed', handleConfirmationProcessed);
    wsClient.on('command_needs_confirmation', handleCommandNeedsConfirmation);
    wsClient.on('interactive_prompt', handleInteractivePrompt);
    wsClient.on('task_needs_user_input', handleTaskNeedsUserInput);
    wsClient.on('chat_aborted', handleChatAborted);
    wsClient.on('agent_stopped', handleAgentStopped);
    wsClient.on('server_error', handleServerError);
    wsClient.on('task_started', handleTaskStarted);
    wsClient.on('task_polling', handleTaskPolling);
    wsClient.on('task_progress', handleTaskProgress);
    wsClient.on('task_completed', handleTaskCompleted);
    wsClient.on('task_failed', handleTaskFailed);
    wsClient.on('task_cancelled', handleTaskCancelled);
    wsClient.on('running_tasks', handleRunningTasks);
    wsClient.on('running_operations', handleRunningOperations);
    wsClient.on('operation_update', handleOperationUpdate);
    wsClient.on('operation_auto_resume_prompt', handleOperationAutoResume);
    wsClient.on('context_usage', handleContextUsage);
    wsClient.on('context_compressed', handleContextCompressed);
    wsClient.on('agent_resumed', handleAgentResumed);

    // 后台长任务运行时定期对账，修复“连接看似正常但实时 task_progress 映射丢失，
    // 刷新页面后才恢复”的场景。后端收到后会重新绑定 taskId -> 当前 socket。
    const reconcileTimer = window.setInterval(() => {
      const st = useChatStore.getState();
      const hasRunningBackgroundTask = st.backgroundTasks.some((t) => t.status === 'running');
      if (!hasRunningBackgroundTask) return;
      wsClient?.socket?.emit('get_running_tasks', { server_id: serverId });
    }, 10000);

    return () => {
      window.clearInterval(reconcileTimer);
      clearProcessingIdleTimer();
      if (thoughtRafRef.current != null) {
        cancelAnimationFrame(thoughtRafRef.current);
        thoughtRafRef.current = null;
      }
      pendingThoughtRef.current = null;
      if (answerRafRef.current != null) {
        cancelAnimationFrame(answerRafRef.current);
        answerRafRef.current = null;
      }
      pendingAnswerRef.current = null;
      if (outputRafRef.current != null) {
        cancelAnimationFrame(outputRafRef.current);
        outputRafRef.current = null;
      }
      pendingOutputRef.current = null;
      useChatStore.getState().setAbortChatFn(null);
      wsClient.off('status', handleStatus);
      wsClient.off('thought_chunk', handleThoughtChunk);
      wsClient.off('tool_call', handleToolCall);
      wsClient.off('command_exec', handleCommandExec);
      wsClient.off('command_output', handleCommandOutput);
      wsClient.off('tool_result', handleToolResult);
      wsClient.off('final_answer', handleFinalAnswer);
      wsClient.off('final_answer_chunk', handleFinalAnswerChunk);
      wsClient.off('final_answer_done', handleFinalAnswerDone);
      wsClient.off('confirmation_required', handleConfirmationRequired);
      wsClient.off('confirmation_processed', handleConfirmationProcessed);
      wsClient.off('command_needs_confirmation', handleCommandNeedsConfirmation);
      wsClient.off('interactive_prompt', handleInteractivePrompt);
      wsClient.off('task_needs_user_input', handleTaskNeedsUserInput);
      wsClient.off('chat_aborted', handleChatAborted);
      wsClient.off('agent_stopped', handleAgentStopped);
      wsClient.off('server_error', handleServerError);
      wsClient.off('task_started', handleTaskStarted);
      wsClient.off('task_polling', handleTaskPolling);
      wsClient.off('task_progress', handleTaskProgress);
      wsClient.off('task_completed', handleTaskCompleted);
      wsClient.off('task_failed', handleTaskFailed);
      wsClient.off('task_cancelled', handleTaskCancelled);
      wsClient.off('running_tasks', handleRunningTasks);
      wsClient.off('running_operations', handleRunningOperations);
      wsClient.off('operation_update', handleOperationUpdate);
      wsClient.off('operation_auto_resume_prompt', handleOperationAutoResume);
      wsClient.off('context_usage', handleContextUsage);
      wsClient.off('context_compressed', handleContextCompressed);
      wsClient.off('agent_resumed', handleAgentResumed);
    };
  }, [wsClient, serverId]);

  const sendMessage = useCallback(
    (message: string) => {
      if (!wsClient?.socket) return;

      const st = useChatStore.getState();
      st.addMessage({ role: 'user', content: message });
      if (processingIdleTimerRef.current != null) {
        window.clearTimeout(processingIdleTimerRef.current);
        processingIdleTimerRef.current = null;
      }
      st.setIsProcessing(true);
      st.clearAgentLiveUi();
      if (message.trim() !== '继续') {
        st.setCurrentOperation(null);
      }

      const mode = useChatStore.getState().chatMode;
      wsClient.socket.emit('chat', {
        server_id: serverId,
        message,
        session_id: sessionId,
        mode,
      });
    },
    [wsClient, serverId, sessionId]
  );

  /** 列表里已存在用户气泡时：仅发 chat（用于编辑后重发，不再 addMessage） */
  const sendChatContinue = useCallback(
    (message: string) => {
      if (!wsClient?.socket) return;
      const st = useChatStore.getState();
      st.setIsProcessing(true);
      st.clearAgentLiveUi();
      if (message.trim() !== '继续') {
        st.setCurrentOperation(null);
      }
      const mode = st.chatMode;
      wsClient.socket.emit('chat', {
        server_id: serverId,
        message,
        session_id: st.sessionId,
        mode,
      });
    },
    [wsClient, serverId]
  );

  const sendConfirmationResponse = useCallback(
    (interruptId: string, approved: boolean) => {
      if (!wsClient?.socket) return;

      wsClient.socket.emit('confirmation_response', {
        interrupt_id: interruptId,
        approved,
      });
    },
    [wsClient]
  );

  const sendInteractiveInput = useCallback(
    (interactiveSessionId: string, input: string) => {
      if (!wsClient?.socket) return;

      const st = useChatStore.getState();
      const bgId = st.interactivePrompt?.backgroundTaskId;
      if (bgId) {
        wsClient.socket.emit('background_task_stdin', {
          task_id: bgId,
          input,
          append_newline: true,
        });
      } else {
        wsClient.socket.emit('interactive_input', {
          session_id: interactiveSessionId,
          input,
        });
      }
      st.hideInteractivePrompt();
    },
    [wsClient]
  );

  const abortChat = useCallback(() => {
    if (!wsClient?.socket) return;

    // 不在此处 clear_session：否则用户点「停止」会清空多轮上下文；需要清空请用界面「清空」
    const st = useChatStore.getState();
    if (processingIdleTimerRef.current != null) {
      window.clearTimeout(processingIdleTimerRef.current);
      processingIdleTimerRef.current = null;
    }
    for (const task of st.backgroundTasks) {
      if (task.status === 'running') {
        wsClient.socket.emit('cancel_task', { task_id: task.taskId });
      }
    }
    wsClient.socket.emit('abort_chat');
    st.setIsProcessing(false);
    st.clearAgentLiveUi();
    if (thoughtRafRef.current != null) {
      cancelAnimationFrame(thoughtRafRef.current);
      thoughtRafRef.current = null;
    }
    pendingThoughtRef.current = null;
    if (answerRafRef.current != null) {
      cancelAnimationFrame(answerRafRef.current);
      answerRafRef.current = null;
    }
    pendingAnswerRef.current = null;
    if (outputRafRef.current != null) {
      cancelAnimationFrame(outputRafRef.current);
      outputRafRef.current = null;
    }
    pendingOutputRef.current = null;
  }, [wsClient]);

  return {
    sendMessage,
    sendChatContinue,
    sendConfirmationResponse,
    sendInteractiveInput,
    abortChat,
  };
}
